import { useSetupFooter } from "views/Setup/context/SetupFooterContext";
import { generateInvoiceRef } from "../utils/generateInvoiceRef";
import { extractSirenFromSiret } from "utils/vatUtils";
import useSettingsStore from "views/hooks/useSettingsStore";
import useNotification from "utils/hooks/useNotification";
import { useLocation, useParams } from "react-router-dom";
import { Card, Col, Form, notification, Row } from "antd";
import useFooterStore from "views/hooks/useFooterStore";
import useGeneralApi from "../../hooks/useGeneralApi";
import useCoreStore from "views/hooks/useCoreStore";
import { ApolloQueryResult } from "@apollo/client";
import { getFormError } from "utils/getFormError";
import { useTranslation } from "react-i18next";
import CardTitle from "components/CardTitle";
import { useEffect, useState } from "react";
import UnitMeasures from "./UnitMeasures";
import Loading from "components/Loading";
import FormatsForm from "./FormatsForm";
import { FaKey } from "react-icons/fa";
import Currency from "./Currency";

interface FormatsFormValues {
  deliveryFormFormat?: string;
  budgetFormat?: string;
  saleOrderFormat?: string;
  purchaseOrderFormat?: string;
  unitMeasures?: ID[];
  returnFormat?: string;
  receiptFormat?: string;
  decimalPlaces?: string | number;
  decimalPlacesForQuantity?: number;
  clientFormat?: string;
  saleOrderFirstNumber?: number;
  returnFirstNumber?: number;
  receiptFirstNumber?: number;
  purchaseOrderFirstNumber?: number;
  deliveryFormFirstNumber?: number;
  budgetFirstNumber?: number;
  paymentInAdvanceFormat?: string;
  paymentInAdvanceFirstNumber?: number;
  paElectronicAddress?: string;
  [key: string]: unknown;
}

interface FormatsPropsTypes {
  refetch?: (
    variables?: Partial<GetVarTypeID> | undefined,
  ) => Promise<ApolloQueryResult<GetCompanySettingResponse>>;
  nextButton?: () => void;
  prevButton?: () => void;
  isSetup?: boolean;
  loading?: boolean;
  refetchCompanySetting?: (
    variables?: Partial<GetVarTypeID> | undefined,
  ) => Promise<ApolloQueryResult<GetCompanySettingResponse>>;
}

export default function Formats(props: FormatsPropsTypes): React.ReactElement {
  const { nextButton, isSetup, refetch, loading, refetchCompanySetting } = props;
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const urlParams: RouteParams = useParams();
  const location = useLocation();
  const tabName = location.pathname.split("/").pop();

  const { updateCompanySetting } = useGeneralApi();
  const { notificationSuccessfullySaved, notificationError } = useNotification();

  const setHandleOkSaveButton = useFooterStore(state => state.setHandleOkSaveButton);
  const handleOkSaveButton = useFooterStore(state => state.handleOkSaveButton);
  const setSaveLoading = useFooterStore(state => state.setSaveLoading);
  const saveLoading = useFooterStore(state => state.saveLoading);
  const data = useCoreStore(state => state.companySettings);
  const currency = useCoreStore(state => state.currency);
  const companyData = useCoreStore(state => state.companyData);

  const suggestedPaElectronicAddress = extractSirenFromSiret(companyData?.vatNumber ?? "");

  const defaultUnitMeasureId = useSettingsStore(state => state.defaultUnitMeasureId);
  const [deliveryExample, setDeliveryExample] = useState("");
  const [invoiceExample, setInvoiceExample] = useState("");
  const [budgetExample, setBudgetExample] = useState("");
  const [ordersExample, setOrdersExample] = useState("");
  const [returnExample, setReturnExample] = useState("");
  const [receiptExample, setReceiptExample] = useState("");
  const [clientExample, setClientExample] = useState("");
  const [paymentInAdvanceExample, setPaymentInAdvanceExample] = useState("");
  const [decimalPlacesForQuantity, setDecimalCasesForQuantity] = useState("");

  const invoiceDate = new Date();

  useEffect(() => {
    if (data) {
      const fieldsData: FieldType[] = [];
      const settings: Record<string, unknown> = { ...data };
      for (const key in data) {
        let value = settings[key];
        if (key === "budgetFormat" && data.budgetFormat) {
          value = data.budgetFormat;
          setBudgetExample(
            generateInvoiceRef(invoiceDate, Number(data.budgetFirstNumber), data.budgetFormat),
          );
        } else if (key === "deliveryFormFormat" && data.deliveryFormFormat) {
          value = data.deliveryFormFormat;
          setDeliveryExample(
            generateInvoiceRef(
              invoiceDate,
              Number(data.deliveryFormFirstNumber),
              data.deliveryFormFormat,
            ),
          );
        } else if (key === "saleOrderFormat" && data.saleOrderFormat) {
          value = data.saleOrderFormat;
          setOrdersExample(
            generateInvoiceRef(
              invoiceDate,
              Number(data.saleOrderFirstNumber),
              data.saleOrderFormat,
            ),
          );
        } else if (key === "purchaseOrderFormat" && data.purchaseOrderFormat) {
          value = data.purchaseOrderFormat;
          setInvoiceExample(
            generateInvoiceRef(
              invoiceDate,
              Number(data.purchaseOrderFirstNumber),
              data.purchaseOrderFormat,
            ),
          );
        } else if (key === "returnFormat" && data.returnFormat) {
          value = data.returnFormat;
          setReturnExample(
            generateInvoiceRef(invoiceDate, Number(data.returnFirstNumber), data.returnFormat),
          );
        } else if (key === "receiptFormat" && data.receiptFormat) {
          value = data.receiptFormat;
          setReceiptExample(
            generateInvoiceRef(invoiceDate, Number(data.receiptFirstNumber), data.receiptFormat),
          );
        } else if (key === "paymentInAdvanceFormat" && data.paymentInAdvanceFormat) {
          value = data.paymentInAdvanceFormat;
          setPaymentInAdvanceExample(
            generateInvoiceRef(
              invoiceDate,
              Number(data.paymentInAdvanceFirstNumber),
              data.paymentInAdvanceFormat,
            ),
          );
        } else if (key === "clientFormat" && data.clientFormat) {
          value = data.clientFormat;
          setClientExample(generateInvoiceRef(invoiceDate, Number(132), data.clientFormat));
        } else if (key === "decimalPlacesForQuantity" && data.decimalPlacesForQuantity) {
          value = data.decimalPlacesForQuantity;
          setDecimalCasesForQuantity(String((123.456789).toFixed(data.decimalPlacesForQuantity)));
        } else if (key === "unitMeasures" && data.unitMeasures) {
          value =
            data.unitMeasures?.length > 0
              ? data.unitMeasures?.map((item: { id: ID }) => item?.id)
              : defaultUnitMeasureId
                ? [defaultUnitMeasureId]
                : [];
        }

        fieldsData.push({
          name: key,
          value: value,
        });
      }
      form.setFields(fieldsData);
      form.setFields([
        {
          name: "returnFirstNumber",
          value: data.purchaseOrderFirstNumber,
        },
        {
          name: "paElectronicAddress",
          value: data.paElectronicAddress || suggestedPaElectronicAddress,
        },
      ]);
    }
  }, [data, defaultUnitMeasureId, suggestedPaElectronicAddress]);

  useEffect(() => {
    if (handleOkSaveButton && tabName === "formats") {
      handleSaveAndNext();
    }
  }, [handleOkSaveButton]);

  const onFinishFailed = ({
    errorFields,
  }: {
    errorFields: { name: (string | number)[]; errors: string[] }[];
  }) => {
    if (errorFields) {
      setHandleOkSaveButton(false);
      setSaveLoading(false);
    }
  };

  const onFinish = async (values: FormatsFormValues) => {
    let errors: ErrorType[] | undefined = [];

    const variables = {
      input: {
        id: urlParams.companyId,
        deliveryFormFormat: values?.deliveryFormFormat,
        budgetFormat: values?.budgetFormat,
        saleOrderFormat: values?.saleOrderFormat,
        purchaseOrderFormat: values?.purchaseOrderFormat,
        unitMeasures: values?.unitMeasures,
        returnFormat: values?.returnFormat,
        receiptFormat: values?.receiptFormat,
        decimalPlaces: Number(values?.decimalPlaces),
        decimalPlacesForQuantity: Number(values?.decimalPlacesForQuantity),
        clientFormat: values?.clientFormat,
        saleOrderFirstNumber: data?.canChangeSaleOrderNextNumber
          ? values?.saleOrderFirstNumber
          : undefined,
        returnFirstNumber: data?.canChangeReturnNextNumber ? values?.returnFirstNumber : undefined,
        receiptFirstNumber: data?.canChangeReceiptNextNumber
          ? values?.receiptFirstNumber
          : undefined,
        purchaseOrderFirstNumber: data?.canChangePurchaseOrderNextNumber
          ? values?.purchaseOrderFirstNumber
          : undefined,
        deliveryFormFirstNumber: data?.canChangeDeliveryFormNextNumber
          ? values?.deliveryFormFirstNumber
          : undefined,
        budgetFirstNumber: data?.canChangeBudgetNextNumber ? values?.budgetFirstNumber : undefined,
        paymentInAdvanceFormat: values?.paymentInAdvanceFormat,
        paymentInAdvanceFirstNumber: data?.canChangePaymentInAdvanceNextNumber
          ? values?.paymentInAdvanceFirstNumber
          : undefined,
        paElectronicAddress: values?.paElectronicAddress,
      },
    };

    const response = await updateCompanySetting({ variables });

    errors = response?.data?.updateCompanySetting?.errors;

    if (errors && errors.length > 0) {
      const fieldsData = getFormError(errors, values);
      notificationError(errors);
      form.setFields(fieldsData);
      setHandleOkSaveButton(false);
      setSaveLoading(false);
    } else {
      notificationSuccessfullySaved(t("Format & Units"));
      nextButton?.();
      setHandleOkSaveButton(false);
      setSaveLoading(false);
      refetchCompanySetting?.();
    }
  };

  const handleSaveAndNext = () => {
    if (!currency) {
      notification.error({
        message: t("Devise"),
        description: t("Please create a currency before proceeding"),
        placement: "bottomLeft",
      });
      setHandleOkSaveButton(false);
      setSaveLoading(false);
      return;
    }
    form.submit();
    setSaveLoading(true);
  };

  useSetupFooter(isSetup ? { onSave: handleSaveAndNext, loading } : null);

  const onValuesChange = (changedValues: Partial<FormatsFormValues>) => {
    if (changedValues?.decimalPlacesForQuantity) {
      setDecimalCasesForQuantity(
        String((123.456789).toFixed(changedValues?.decimalPlacesForQuantity)),
      );
    }
    if (changedValues?.decimalPlacesForQuantity) {
      setDecimalCasesForQuantity(
        String((123.456789).toFixed(changedValues?.decimalPlacesForQuantity)),
      );
    }

    const formats = {
      budgetFormat: {
        example: setBudgetExample,
        number: Number(data?.budgetFirstNumber),
      },
      deliveryFormFormat: {
        example: setDeliveryExample,
        number: Number(data?.deliveryFormFirstNumber),
      },
      saleOrderFormat: {
        example: setOrdersExample,
        number: Number(data?.budgetFirstNumber),
      },
      purchaseOrderFormat: {
        example: setInvoiceExample,
        number: Number(data?.purchaseOrderFirstNumber),
      },
      returnFormat: {
        example: setReturnExample,
        number: Number(data?.returnFirstNumber),
      },
      receiptFormat: {
        example: setReceiptExample,
        number: Number(data?.receiptFirstNumber),
      },
      clientFormat: {
        example: setClientExample,
        number: Number(123),
      },
      paymentInAdvanceFormat: {
        example: setPaymentInAdvanceExample,
        number: Number(data?.paymentInAdvanceFirstNumber),
      },
    };

    Object.entries(formats).forEach(([formatKey, formatValue]) => {
      const changedFormat = (changedValues as Record<string, string | undefined>)?.[formatKey];
      if (changedFormat) {
        const { example, number } = formatValue;
        example(generateInvoiceRef(invoiceDate, number, changedFormat));
      }
    });
  };

  return (
    <>
      {loading || saveLoading ? (
        <Loading />
      ) : (
        <Form
          name="createUpdateFormats"
          layout="vertical"
          form={form}
          onFinish={onFinish}
          autoComplete="off"
          className="format-form"
          initialValues={{
            unitMeasures: defaultUnitMeasureId ? [defaultUnitMeasureId] : [],
          }}
          onValuesChange={onValuesChange}
          onFinishFailed={onFinishFailed}
        >
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={24}>
                  <Card
                    title={<CardTitle icon={<FaKey />} title={t("Numeration")} />}
                    className="antd-newrai-card"
                  >
                    <FormatsForm
                      form={form}
                      data={data as unknown as GetCompanySettingResponseData}
                      budgetExample={budgetExample}
                      clientExample={clientExample}
                      ordersExample={ordersExample}
                      returnExample={returnExample}
                      invoiceExample={invoiceExample}
                      receiptExample={receiptExample}
                      deliveryExample={deliveryExample}
                      decimalPlacesForQuantity={decimalPlacesForQuantity}
                      paymentInAdvanceExample={paymentInAdvanceExample}
                      suggestedPaElectronicAddress={suggestedPaElectronicAddress}
                    />
                  </Card>
                </Col>
              </Row>
            </Col>
            <Col span={12}>
              <Row gutter={[16, 16]}>
                <Col xs={12} sm={24}>
                  <UnitMeasures />
                </Col>
                <Col xs={12} sm={24}>
                  <Currency refetch={refetch} loading={loading} isSetup={isSetup} />
                </Col>
              </Row>
            </Col>
          </Row>
        </Form>
      )}
    </>
  );
}
